import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { Banner, Card, SectionTitle } from "../components/ui";
import { ApiError, uploadToSignedUrl, type Api } from "../lib/api";
import type {
  Account,
  AuditEvent,
  BuildArtifact,
  BuildCandidate,
  LicenseType,
  PortalDocument,
  PortalLicense,
  ReleaseLine,
  ReleaseLineDef,
  UploadRecord,
} from "../lib/types";

const ALL_LINES: ReleaseLine[] = ["release-avx256-rl10", "release-rpm-el10"];

const ADMIN_SECTIONS: Array<{ path: string; label: string }> = [
  { path: "builds", label: "Builds" },
  { path: "documents", label: "Documents" },
  { path: "licenses", label: "Licenses" },
  { path: "logs", label: "Logs" },
  { path: "activity", label: "Activity" },
];

/** Admin console — platform_admin only. Deep-linkable sub-tabs under /app/admin/*. §4.5 */
export function Admin({ api }: { api: Api }) {
  return (
    <div>
      <SectionTitle
        kicker="Admin · Platform"
        title="Builds & support"
        desc="Manage GA builds, documents, licenses, and support logs. Manage accounts and users on the Accounts tab; triage customer cases on the Support tab."
      />
      <nav className="mt-6 flex flex-wrap gap-1 border-b border-line">
        {ADMIN_SECTIONS.map((s) => (
          <NavLink
            key={s.path}
            to={s.path}
            className={({ isActive }) =>
              `rounded-t-lg px-4 py-2 text-sm font-medium transition ${
                isActive
                  ? "border-b-2 border-accent-strong text-accent-strong"
                  : "text-ink-muted hover:text-ink"
              }`
            }
          >
            {s.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-6">
        <Routes>
          <Route index element={<Navigate to="builds" replace />} />
          <Route path="builds" element={<Builds api={api} />} />
          <Route path="documents" element={<Documents api={api} />} />
          <Route path="licenses" element={<Licenses api={api} />} />
          <Route path="logs" element={<SupportLogs api={api} />} />
          <Route path="activity" element={<Activity api={api} />} />
          <Route path="*" element={<Navigate to="builds" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function bytes(n: number): string {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Build catalog + GA promotion (§4.5). Browse real objects under each release-line
 * prefix in the signing bucket; promote/demote to control what customers see. Only the
 * newest N GA builds per line stay live — the backend auto-prunes older promotions.
 */
function buildKindLabel(kind: BuildArtifact["kind"]): string {
  return kind === "portal" ? "Portal RPM" : kind === "selinux-policy" ? "SELinux policy RPM" : "Tarball";
}

export function Builds({ api }: { api: Api }) {
  const [lines, setLines] = useState<Record<string, BuildCandidate[]> | null>(null);
  const [defs, setDefs] = useState<ReleaseLineDef[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function load() {
    setError(null);
    api
      .listBuildCandidates()
      .then((r) => setLines(r.lines))
      .catch(() => setError("Could not load builds."));
  }

  useEffect(() => {
    load();
    api
      .listReleaseLines()
      .then((r) => setDefs(r.lines))
      .catch(() => setDefs(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(c: BuildCandidate) {
    setError(null);
    setBusy(c.objectPath);
    try {
      if (c.ga) await api.demoteBuild(c.pipeline, c.objectPath);
      else if (c.pipeline === "release-rpm-el10") {
        const mate = (lines?.[c.pipeline] ?? []).find((other) => other.artifact && c.artifact &&
          other.artifact.version === c.artifact.version && other.artifact.buildNumber === c.artifact.buildNumber &&
          other.artifact.kind !== c.artifact.kind && other.objectPath !== c.objectPath);
        if (!mate?.artifact || !c.artifact || !c.artifact.kind) throw new Error("Register both matching portal and SELinux policy RPMs first.");
        await api.promotePair(c.artifact.kind === "portal" ? c.objectPath : mate.objectPath,
          c.artifact.kind === "portal" ? mate.objectPath : c.objectPath);
      } else await api.promoteBuild(c.pipeline, c.objectPath);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(null);
    }
  }

  // Prefer the backend registry of tracks; fall back to the static pair pre-fetch.
  const lineIds: string[] = defs ? defs.map((d) => d.id) : ALL_LINES;
  const labelFor = (id: string) => defs?.find((d) => d.id === id)?.label ?? id;

  return (
    <>
      <UploadBuild api={api} defs={defs} onDone={load} />
      <Card>
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">Builds &amp; GA promotion</h2>
            <p className="text-sm text-ink-muted">
              Customers only see builds marked <span className="text-accent-strong">GA</span> and
              within the retention window (<span className="font-mono">live</span>).
            </p>
          </div>
          <button className="btn-ghost" onClick={load}>Refresh</button>
        </div>
        {error && <Banner tone="error">{error}</Banner>}
        {!lines && <p className="text-sm text-ink-muted">Loading…</p>}
        {lines &&
          lineIds.map((line) => {
            const rows = lines[line] ?? [];
            const liveCount = rows.filter((r) => r.live).length;
            return (
              <div key={line} className="mb-6 last:mb-0">
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="font-mono text-sm text-ink">{labelFor(line)}</h3>
                  <span className="text-xs text-ink-dim">{liveCount} live</span>
                </div>
              {rows.length === 0 && (
                <p className="text-sm text-ink-muted">No objects under this release line yet.</p>
              )}
              {line === "release-rpm-el10" && <p className="mb-2 text-xs text-ink-muted">Portal and SELinux policy RPMs must have the same version and build number; only complete committed pairs are downloadable.</p>}
              {rows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-dim">
                        <th className="py-2 pr-4">Object</th>
                        <th className="py-2 pr-4">Kind</th>
                        <th className="py-2 pr-4">Version</th>
                        <th className="py-2 pr-4">Size</th>
                        <th className="py-2 pr-4">State</th>
                        <th className="py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-xs text-ink-muted">
                      {rows.map((c) => (
                        <tr key={c.objectPath} className="border-b border-line/50">
                          <td className="py-2 pr-4 font-mono max-w-xs truncate" title={c.objectPath}>
                            {c.objectPath}
                          </td>
                          <td className="py-2 pr-4">{c.artifact ? buildKindLabel(c.artifact.kind) : "—"}</td>
                          <td className="py-2 pr-4">{c.artifact?.version ?? "—"}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">{bytes(c.sizeBytes)}</td>
                          <td className="py-2 pr-4">
                            {c.ga ? (
                              <span className={c.live ? "text-accent-strong" : "text-ink-dim"}>
                                GA{c.live ? " · live" : " · aged out"}
                              </span>
                            ) : (
                              <span className="text-ink-dim">not promoted</span>
                            )}
                          </td>
                          <td className="py-2">
                            <button
                              className={c.ga ? "btn-ghost" : "btn-primary"}
                              disabled={busy === c.objectPath || (line === "release-rpm-el10" && !c.ga &&
                                !rows.some((other) => other.artifact && c.artifact && other.objectPath !== c.objectPath &&
                                  other.artifact.kind !== c.artifact.kind && other.artifact.version === c.artifact.version &&
                                  other.artifact.buildNumber === c.artifact.buildNumber))}
                              onClick={() => toggle(c)}
                            >
                              {busy === c.objectPath ? "…" : c.ga ? "Demote" : line === "release-rpm-el10" ? "Promote pair" : "Promote to GA"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
              );
            })}
      </Card>
    </>
  );
}

/**
 * Admin build upload + registration (§4.5, "define a build" framework). Pick a release
 * line, upload the tarball straight to the signing bucket via a write-only signed URL,
 * then register its metadata so it becomes a promotable candidate. Works for any track
 * the backend registry defines, so a new line (e.g. Windows) needs no code change here.
 */
type RpmUploadOutcome = {
  file: File;
  sha256: string;
  slot: "portal" | "policy";
  state: "Waiting" | "Uploading" | "Uploaded, registering" | "Registered" | "Upload failed" | "Uploaded, registration failed";
  pct?: number;
  artifact?: BuildArtifact;
  error?: string;
};

export function UploadBuild({
  api,
  defs,
  onDone,
}: {
  api: Api;
  defs: ReleaseLineDef[] | null;
  onDone: () => void;
}) {
  const lineDefs: ReleaseLineDef[] =
    defs ??
    ALL_LINES.map((id) => ({ id, label: id, osFamily: "linux", defaultOs: "", defaultArch: "" }));
  const [line, setLine] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [policyFileInputKey, setPolicyFileInputKey] = useState(0);
  const [version, setVersion] = useState("");
  const [buildNumber, setBuildNumber] = useState("");
  const [sha256, setSha256] = useState("");
  const [policySha256, setPolicySha256] = useState("");
  const [releaseNotesUrl, setReleaseNotesUrl] = useState("");
  const [pct, setPct] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<RpmUploadOutcome[]>([]);
  const [runningFile, setRunningFile] = useState<string | null>(null);

  const activeLine = line || lineDefs[0]?.id || "";
  const isRpm = activeLine === "release-rpm-el10";

  async function submit() {
    setError(null);
    setOk(null);
    setOutcomes([]);
    if (!activeLine) return setError("Select a release line.");
    if (isRpm) {
      if (!file && !policyFile) return setError("Choose a build file to upload.");
      if (file && policyFile && file.name.includes("-selinux-") === policyFile.name.includes("-selinux-")) {
        return setError("Choose one Portal RPM and one SELinux policy RPM (exactly one filename must contain -selinux-).");
      }
      const results: RpmUploadOutcome[] = [
        ...(file ? [{ file, sha256, slot: "portal" as const, state: "Waiting" as const }] : []),
        ...(policyFile ? [{ file: policyFile, sha256: policySha256, slot: "policy" as const, state: "Waiting" as const }] : []),
      ].sort((a, b) => Number(a.file.name.includes("-selinux-")) - Number(b.file.name.includes("-selinux-")));
      setBusy(true);
      setOutcomes(results.map((result) => ({ ...result })));
      try {
        for (const result of results) {
          setRunningFile(result.file.name);
          setPct(0);
          result.state = "Uploading";
          setOutcomes(results.map((outcome) => ({ ...outcome })));
          try {
            const mint = await api.mintBuildUploadUrl(activeLine, result.file.name, result.file.type || undefined);
            await uploadToSignedUrl(mint.url, result.file, (p) => {
              setPct(p);
              result.pct = p;
              setOutcomes(results.map((outcome) => ({ ...outcome })));
            });
            result.state = "Uploaded, registering";
            setPct(null);
            setOutcomes(results.map((outcome) => ({ ...outcome })));
            const { artifact } = await api.registerBuild({
              line: activeLine,
              objectPath: mint.objectPath,
              buildNumber: buildNumber.trim() || undefined,
              sha256: result.sha256.trim() || undefined,
              releaseNotesUrl: releaseNotesUrl.trim() || undefined,
            });
            result.artifact = artifact;
            result.state = "Registered";
            if (result.slot === "portal") {
              setFile(null);
              setSha256("");
              setFileInputKey((key) => key + 1);
            } else {
              setPolicyFile(null);
              setPolicySha256("");
              setPolicyFileInputKey((key) => key + 1);
            }
          } catch (e) {
            result.state = result.state === "Uploaded, registering" ? "Uploaded, registration failed" : "Upload failed";
            result.error = e instanceof Error ? e.message : "Upload failed.";
          }
          setOutcomes(results.map((outcome) => ({ ...outcome })));
        }
        const registered = results.flatMap((result) => result.artifact ? [result.artifact] : []);
        const first = registered[0];
        const second = registered[1];
        const paired = first && second && first.version === second.version && first.buildNumber === second.buildNumber &&
          ((first.kind === "portal" && second.kind === "selinux-policy") ||
            (first.kind === "selinux-policy" && second.kind === "portal"));
        if (registered.length) {
          setVersion(first!.version);
          setOk(`${registered.map((artifact) => `Registered ${buildKindLabel(artifact.kind)} ${artifact.version} (${artifact.objectPath}).`).join(" ")} ${paired ? "Both halves registered — Promote the pair below." : "Register the matching RPM before promoting the pair."}`);
        }
        if (results.some((result) => result.error)) {
          setError(results.some((result) => result.state === "Registered" || result.state === "Uploaded, registration failed")
            ? "Some RPMs could not be registered. See per-file results below."
            : "Some RPMs could not be uploaded. See per-file results below.");
        } else {
          setBuildNumber("");
          setReleaseNotesUrl("");
        }
        onDone();
      } finally {
        setBusy(false);
        setPct(null);
        setRunningFile(null);
      }
      return;
    }
    if (!file) return setError("Choose a build file to upload.");
    if (activeLine !== "release-rpm-el10" && !version.trim()) return setError("Enter a version.");
    setBusy(true);
    setPct(0);
    try {
      const mint = await api.mintBuildUploadUrl(activeLine, file.name, file.type || undefined);
      await uploadToSignedUrl(mint.url, file, (p) => setPct(p));
      const { artifact } = await api.registerBuild({
        line: activeLine,
        objectPath: mint.objectPath,
        version: version.trim() || undefined,
        buildNumber: buildNumber.trim() || undefined,
        sha256: sha256.trim() || undefined,
        releaseNotesUrl: releaseNotesUrl.trim() || undefined,
      });
      setOk(`Registered ${artifact.version} (${artifact.objectPath}). Promote it to GA below.`);
      setFile(null);
      setFileInputKey((key) => key + 1);
      setVersion("");
      setBuildNumber("");
      setSha256("");
      setReleaseNotesUrl("");
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
      setPct(null);
    }
  }

  return (
    <Card className="mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-ink">Upload a build</h2>
        <p className="text-sm text-ink-muted">
          Uploads a tarball or RPM to the signing bucket and registers it as a candidate. RPMs
          stay hidden until both matching portal and SELinux policy files are promoted together.
        </p>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {ok && <Banner tone="success">{ok}</Banner>}
      {isRpm && outcomes.length > 0 && <ul className="mb-4 space-y-1 text-sm text-ink-muted" aria-live="polite">
        {outcomes.map((outcome) => <li key={outcome.slot}>
          {outcome.file.name}: {outcome.state}{outcome.state === "Uploading" && outcome.pct !== undefined ? ` ${outcome.pct}%` : ""}. {outcome.error}
        </li>)}
      </ul>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Release line</span>
          <select
            className="input"
            value={activeLine}
            onChange={(e) => {
              setLine(e.target.value);
              setFile(null);
              setPolicyFile(null);
              setVersion("");
              setBuildNumber("");
              setReleaseNotesUrl("");
              setSha256("");
              setPolicySha256("");
              setOutcomes([]);
              setOk(null);
              setError(null);
              setFileInputKey((key) => key + 1);
              setPolicyFileInputKey((key) => key + 1);
            }}
            disabled={busy}
          >
            {lineDefs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        {isRpm ? <div className="grid gap-3 sm:col-span-2">
          {([
            { label: "Portal RPM", setFile, sha256, setSha256, inputKey: fileInputKey },
            { label: "SELinux policy RPM", setFile: setPolicyFile, sha256: policySha256, setSha256: setPolicySha256, inputKey: policyFileInputKey },
          ]).map((slot) => <div key={slot.label} className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">{slot.label}</span>
              <input key={slot.inputKey} className="input" type="file" disabled={busy}
                onChange={(e) => { slot.setFile(e.target.files?.[0] ?? null); setVersion(""); }} />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">SHA-256 (optional)</span>
              <input className="input font-mono" placeholder="hex digest" value={slot.sha256}
                onChange={(e) => slot.setSha256(e.target.value)} disabled={busy} />
            </label>
          </div>)}
        </div> : <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Build tarball or RPM</span>
          <input
            key={fileInputKey}
            className="input"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </label>}
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Version {activeLine === "release-rpm-el10" ? "(derived from filename)" : ""}</span>
          <input
            className="input"
            placeholder={isRpm ? "Derived by server from filename" : "1.4.0"}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={busy}
            readOnly={isRpm || undefined}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Build number (optional)</span>
          <input
            className="input"
            placeholder="200"
            value={buildNumber}
            onChange={(e) => setBuildNumber(e.target.value)}
            disabled={busy}
          />
        </label>
        {!isRpm && <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">SHA-256 (optional)</span>
          <input
            className="input font-mono"
            placeholder="hex digest"
            value={sha256}
            onChange={(e) => setSha256(e.target.value)}
            disabled={busy}
          />
        </label>}
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Release notes URL (optional)</span>
          <input
            className="input"
            placeholder="https://…"
            value={releaseNotesUrl}
            onChange={(e) => setReleaseNotesUrl(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? (pct !== null ? `Uploading ${pct}%` : "Working…") : "Upload & register"}
        </button>
        {busy && pct !== null && (
          <span className="text-xs text-ink-dim">{isRpm ? runningFile : file?.name}</span>
        )}
      </div>
    </Card>
  );
}

/**
 * Documents library management. Publish a PDF (upload to the private documents bucket via a
 * write-only signed URL, then record it with a title) so every provisioned customer sees it
 * on their Documents tab, and delete published documents. Documents are global, not
 * entitlement-scoped.
 */
function Documents({ api }: { api: Api }) {
  const [docs, setDocs] = useState<PortalDocument[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [pct, setPct] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [delBusy, setDelBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  function load() {
    api
      .listAdminDocuments()
      .then((r) => setDocs(r.documents))
      .catch(() => setError("Could not load documents."));
  }

  useEffect(load, [api]);

  async function submit() {
    setError(null);
    setOk(null);
    if (!file) return setError("Choose a PDF to upload.");
    if (!title.trim()) return setError("Enter a title.");
    setBusy(true);
    setPct(0);
    try {
      const mint = await api.mintDocumentUploadUrl(file.name, file.type || undefined);
      await uploadToSignedUrl(mint.url, file, (p) => setPct(p));
      const { document } = await api.publishDocument({
        objectPath: mint.objectPath,
        title: title.trim(),
        description: description.trim() || undefined,
        category: category.trim() || undefined,
        contentType: file.type || undefined,
      });
      setOk(`Published “${document.title}”. Customers can see it now.`);
      setFile(null);
      setTitle("");
      setDescription("");
      setCategory("");
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Publish failed.");
    } finally {
      setBusy(false);
      setPct(null);
    }
  }

  async function remove(d: PortalDocument) {
    if (!window.confirm(`Delete “${d.title}”? Customers will no longer see it.`)) return;
    setDelBusy(d.id);
    setError(null);
    try {
      await api.deleteDocument(d.id);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Delete failed.");
    } finally {
      setDelBusy(null);
    }
  }

  return (
    <Card>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-ink">Documents library</h2>
        <p className="text-sm text-ink-muted">
          Publish PDFs with a title to the shared customer documents library. Every provisioned
          customer sees the published set on their Documents tab.
        </p>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {ok && <Banner tone="success">{ok}</Banner>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">PDF file</span>
          <input
            className="input"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Title</span>
          <input
            className="input"
            placeholder="Installation guide"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Category (optional)</span>
          <input
            className="input"
            placeholder="Guides"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Description (optional)</span>
          <input
            className="input"
            placeholder="Short summary shown under the title"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? (pct !== null ? `Uploading ${pct}%` : "Working…") : "Upload & publish"}
        </button>
        {busy && pct !== null && <span className="text-xs text-ink-dim">{file?.name}</span>}
      </div>

      <div className="mt-6">
        {!docs && <p className="text-sm text-ink-muted">Loading…</p>}
        {docs && docs.length === 0 && (
          <p className="text-sm text-ink-muted">No documents published yet.</p>
        )}
        {docs && docs.length > 0 && (
          <div className="grid gap-2">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex flex-col gap-2 rounded-lg border border-line p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{d.title}</span>
                    {d.category && <span className="badge-accent">{d.category}</span>}
                  </div>
                  <div className="mt-1 text-xs text-ink-dim">
                    {d.filename} · {bytes(d.sizeBytes)} ·{" "}
                    {new Date(d.publishedAt).toLocaleDateString()} · {d.publishedBy}
                  </div>
                </div>
                <button
                  className="btn-ghost text-danger"
                  disabled={delBusy === d.id}
                  onClick={() => void remove(d)}
                >
                  {delBusy === d.id ? "Deleting…" : "Delete"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function licenseTypeLabel(t: LicenseType): string {
  return t === "hub" ? "Hub" : "Named user";
}

/**
 * Licenses admin tab — issue RSA-4096/SHA-256-signed customer license files (.lic) the
 * product verifies offline. Two SKUs only: Hub (max_hubs) and Named user (max_users), each
 * with a count and a 1–10 year term. Bind to a known portal/HubSpot account or a free-form
 * customer. Every issued license is recorded so it can be re-downloaded or revoked.
 */
function Licenses({ api }: { api: Api }) {
  const [licenses, setLicenses] = useState<PortalLicense[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [hubOn, setHubOn] = useState(true);
  const [hubCount, setHubCount] = useState("1");
  const [hubTerm, setHubTerm] = useState("1");
  const [userOn, setUserOn] = useState(true);
  const [userCount, setUserCount] = useState("1");
  const [userTerm, setUserTerm] = useState("1");
  const [companyId, setCompanyId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [emailId, setEmailId] = useState<string | null>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [includeCertificate, setIncludeCertificate] = useState(true);
  const [emailResults, setEmailResults] = useState<
    Record<string, { ok: boolean; message: string }>
  >({});

  function load() {
    api
      .listLicenses()
      .then((r) => {
        setLicenses(r.licenses);
        setEnabled(r.enabled);
      })
      .catch(() => setError("Could not load licenses."));
    api
      .listAccounts()
      .then((r) => setAccounts(r.accounts))
      .catch(() => setAccounts([]));
  }

  useEffect(load, [api]);

  useEffect(() => {
    const license = licenses?.find((l) => l.id === emailId);
    if (!license?.companyId) return;
    let current = true;
    api.listAccountUsers(license.companyId)
      .then(({ users }) => {
        if (current) {
          setTo((value) => value || users.filter((u) => u.status === "active")
            .slice(0, 11)
            .map((u) => u.email)
            .join(", "));
        }
      })
      .catch(() => { /* Directory hints are optional; recipients remain admin-entered. */ });
    return () => {
      current = false;
    };
  }, [api, emailId, licenses]);

  function saveBlob(filename: string, pem: string) {
    const blob = new Blob([pem], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function submit() {
    setError(null);
    setOk(null);
    // Build the meters to issue. A customer license spans two independent SKUs —
    // Hub capacity and Named-user capacity — each with its own count and term.
    // Either or both may be issued; each produces its own signed .lic file.
    const meters: { type: LicenseType; count: number; termYears: number }[] = [];
    if (hubOn) {
      const c = Number(hubCount);
      const t = Number(hubTerm);
      if (!Number.isFinite(c) || c < 1) return setError("Hub count must be a positive integer.");
      if (!Number.isFinite(t) || t < 1 || t > 10)
        return setError("Hub term must be 1–10 years.");
      meters.push({ type: "hub", count: c, termYears: t });
    }
    if (userOn) {
      const c = Number(userCount);
      const t = Number(userTerm);
      if (!Number.isFinite(c) || c < 1)
        return setError("Named-user count must be a positive integer.");
      if (!Number.isFinite(t) || t < 1 || t > 10)
        return setError("Named-user term must be 1–10 years.");
      meters.push({ type: "named_user", count: c, termYears: t });
    }
    if (meters.length === 0) return setError("Enable at least one meter (Hub or Named user).");
    if (!companyId && (!customerName.trim() || !customerId.trim()))
      return setError("Select an account, or enter both a customer name and id.");
    setBusy(true);
    try {
      const issued: PortalLicense[] = [];
      for (const m of meters) {
        const { license } = await api.createLicense({
          type: m.type,
          count: m.count,
          termYears: m.termYears,
          companyId: companyId || undefined,
          customerName: companyId ? undefined : customerName.trim(),
          customerId: companyId ? undefined : customerId.trim(),
          notes: notes.trim() || undefined,
        });
        saveBlob(`pumamesh-${license.id}.lic`, license.pem);
        issued.push(license);
      }
      const who = issued[0]?.customerName ?? "customer";
      setOk(
        `Issued ${issued.map((l) => `${licenseTypeLabel(l.type)} (${l.count})`).join(" + ")} for ${who}. Use the per-row Email buttons below to send them; no email was sent automatically.`,
      );
      setHubCount("1");
      setHubTerm("1");
      setUserCount("1");
      setUserTerm("1");
      setNotes("");
      setCustomerName("");
      setCustomerId("");
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Issue failed.");
    } finally {
      setBusy(false);
    }
  }

  async function download(l: PortalLicense) {
    setRowBusy(l.id);
    setError(null);
    try {
      const { pem, filename } = await api.downloadLicense(l.id);
      saveBlob(filename, pem);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Download failed.");
    } finally {
      setRowBusy(null);
    }
  }

  async function downloadCertificate(l: PortalLicense) {
    setRowBusy(l.id);
    setError(null);
    try {
      const { certificate, filename } = await api.downloadLicenseCertificate(l.id);
      saveBlob(filename, certificate);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Certificate download failed.");
    } finally {
      setRowBusy(null);
    }
  }

  function openEmail(l: PortalLicense) {
    setEmailId(l.id);
    setTo("");
    setCc("");
    setIncludeCertificate(true);
    setEmailResults((results) => {
      const next = { ...results };
      delete next[l.id];
      return next;
    });
  }

  async function sendEmail(l: PortalLicense) {
    const recipients = (input: string) => input.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
    const toAddresses = recipients(to);
    const ccAddresses = recipients(cc);
    if (toAddresses.length === 0) {
      setEmailResults((results) => ({
        ...results, [l.id]: { ok: false, message: "Enter at least one To address." },
      }));
      return;
    }
    setRowBusy(l.id);
    setEmailResults((results) => {
      const next = { ...results };
      delete next[l.id];
      return next;
    });
    try {
      const result = await api.emailLicense(l.id, {
        to: toAddresses, cc: ccAddresses, includeCertificate,
      });
      setEmailResults((results) => ({
        ...results,
        [l.id]: { ok: true, message: `Sent to ${result.to.join(", ")}${result.cc.length ? `; CC: ${result.cc.join(", ")}` : ""}.` },
      }));
      setEmailId((id) => id === l.id ? null : id);
    } catch (e) {
      setEmailResults((results) => ({
        ...results, [l.id]: { ok: false, message: e instanceof ApiError ? e.message : "Email failed." },
      }));
    } finally {
      setRowBusy(null);
    }
  }

  async function revoke(l: PortalLicense) {
    if (!window.confirm(`Revoke this ${licenseTypeLabel(l.type)} license for ${l.customerName}?`))
      return;
    setRowBusy(l.id);
    setError(null);
    try {
      await api.revokeLicense(l.id);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Revoke failed.");
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <Card>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-ink">Licenses</h2>
        <p className="text-sm text-ink-muted">
          Issue signed customer license files (.lic) the product verifies offline. Two meters:
          Hub capacity and Named-user capacity, each with a count and a 1–10 year term.
        </p>
      </div>
      {!enabled && (
        <Banner tone="error">
          License signing is not configured (no signing key). Set the license-signing-private-key
          secret to enable issuance.
        </Banner>
      )}
      {error && <Banner tone="error">{error}</Banner>}
      {ok && <Banner tone="success">{ok}</Banner>}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-line p-3 sm:col-span-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-ink">
            <input
              type="checkbox"
              checked={hubOn}
              onChange={(e) => setHubOn(e.target.checked)}
              disabled={busy}
            />
            Hub license (PM-HUB-12M — max hubs)
          </label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Hub count</span>
              <input
                className="input"
                type="number"
                min={1}
                value={hubCount}
                onChange={(e) => setHubCount(e.target.value)}
                disabled={busy || !hubOn}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Term (years, 1–10)</span>
              <input
                className="input"
                type="number"
                min={1}
                max={10}
                value={hubTerm}
                onChange={(e) => setHubTerm(e.target.value)}
                disabled={busy || !hubOn}
              />
            </label>
          </div>
        </div>
        <div className="rounded-lg border border-line p-3 sm:col-span-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-ink">
            <input
              type="checkbox"
              checked={userOn}
              onChange={(e) => setUserOn(e.target.checked)}
              disabled={busy}
            />
            Named-user license (PM-NAMED-USER-12M — max users)
          </label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Named-user count</span>
              <input
                className="input"
                type="number"
                min={1}
                value={userCount}
                onChange={(e) => setUserCount(e.target.value)}
                disabled={busy || !userOn}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Term (years, 1–10)</span>
              <input
                className="input"
                type="number"
                min={1}
                max={10}
                value={userTerm}
                onChange={(e) => setUserTerm(e.target.value)}
                disabled={busy || !userOn}
              />
            </label>
          </div>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Account</span>
          <select
            className="input"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={busy}
          >
            <option value="">— Free-form customer —</option>
            {accounts.map((a) => (
              <option key={a.companyId} value={a.companyId}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        {!companyId && (
          <>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Customer name</span>
              <input
                className="input"
                placeholder="Acme Corp"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-muted">Customer id</span>
              <input
                className="input"
                placeholder="acme"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1 block text-ink-muted">Notes (optional)</span>
          <input
            className="input"
            placeholder="PO number, renewal, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <div className="mt-4">
        <button className="btn-primary" onClick={submit} disabled={busy || !enabled}>
          {busy ? "Issuing…" : "Issue & download license(s)"}
        </button>
      </div>

      <div className="mt-6">
        {!licenses && <p className="text-sm text-ink-muted">Loading…</p>}
        {licenses && licenses.length === 0 && (
          <p className="text-sm text-ink-muted">No licenses issued yet.</p>
        )}
        {licenses && licenses.length > 0 && (
          <div className="grid gap-2">
            {licenses.map((l) => (
              <div key={l.id} className="rounded-lg border border-line p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-ink">{l.customerName}</span>
                      <span className="badge-accent">{licenseTypeLabel(l.type)}</span>
                      <span className="text-xs text-ink-muted">
                        {l.count} × {l.termYears}y
                      </span>
                      {l.status === "revoked" && <span className="badge text-danger">Revoked</span>}
                    </div>
                    <div className="mt-1 text-xs text-ink-dim">
                      {l.productSku} · {new Date(l.activationStart).toLocaleDateString()} →{" "}
                      {new Date(l.activationEnd).toLocaleDateString()} · {l.createdByEmail}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className="btn-ghost"
                      disabled={rowBusy === l.id}
                      onClick={() => void download(l)}
                    >
                      Download
                    </button>
                    <button
                      className="btn-ghost"
                      disabled={rowBusy === l.id}
                      onClick={() => void downloadCertificate(l)}
                    >
                      Certificate
                    </button>
                    {l.status === "active" && (
                      <button
                        className="btn-ghost"
                        disabled={rowBusy === l.id}
                        onClick={() => emailId === l.id ? setEmailId(null) : openEmail(l)}
                      >
                        Email
                      </button>
                    )}
                    {l.status === "active" && (
                      <button
                        className="btn-ghost text-danger"
                        disabled={rowBusy === l.id}
                        onClick={() => void revoke(l)}
                      >
                        {rowBusy === l.id ? "Working…" : "Revoke"}
                      </button>
                    )}
                  </div>
                </div>
                {emailId === l.id && (
                  <div className="mt-3 border-t border-line pt-3">
                    <p className="mb-3 text-xs text-ink-muted">
                      Review To recipients before sending. Active account users are suggested when available.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="block text-sm">
                        <span className="mb-1 block text-ink-muted">To (comma or newline-separated)</span>
                        <input
                          className="input"
                          type="text"
                          value={to}
                          onChange={(e) => setTo(e.target.value)}
                          disabled={rowBusy === l.id}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-ink-muted">CC (optional, comma or newline-separated)</span>
                        <input
                          className="input"
                          type="text"
                          value={cc}
                          onChange={(e) => setCc(e.target.value)}
                          disabled={rowBusy === l.id}
                        />
                      </label>
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
                      <input
                        type="checkbox"
                        checked={includeCertificate}
                        onChange={(e) => setIncludeCertificate(e.target.checked)}
                        disabled={rowBusy === l.id}
                      />
                      Include certificate
                    </label>
                    <div className="mt-3 flex items-center gap-2">
                      <button className="btn-primary" disabled={rowBusy === l.id} onClick={() => void sendEmail(l)}>
                        {rowBusy === l.id ? "Sending…" : "Send"}
                      </button>
                      <button className="btn-ghost" disabled={rowBusy === l.id} onClick={() => setEmailId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {emailResults[l.id] && (
                  <div className="mt-3">
                    <Banner tone={emailResults[l.id]?.ok ? "success" : "error"}>
                      {emailResults[l.id]?.message}
                    </Banner>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Support log retrieval (§4.4/§4.5). List recorded uploads and mint a short-lived read URL. */
function SupportLogs({ api }: { api: Api }) {
  const [uploads, setUploads] = useState<UploadRecord[] | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function load() {
    setError(null);
    api
      .listUploads(filter.trim() || undefined)
      .then((r) => setUploads(r.uploads))
      .catch(() => setError("Could not load uploads."));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function download(u: UploadRecord) {
    setError(null);
    setBusy(u.objectPath);
    try {
      const res = await api.logDownloadUrl(u.bucket, u.objectPath);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not mint download URL.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink">Support logs</h2>
          <p className="text-sm text-ink-muted">
            Customer log uploads. Downloads mint a short-lived signed URL and are audited.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            className="input !w-56"
            placeholder="Filter by account ID"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="btn-ghost" onClick={load}>Refresh</button>
        </div>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {uploads && uploads.length === 0 && (
        <p className="text-sm text-ink-muted">No uploads recorded.</p>
      )}
      {uploads && uploads.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-dim">
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4">Account</th>
                <th className="py-2 pr-4">Object</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody className="text-xs text-ink-muted">
              {uploads.map((u) => (
                <tr key={`${u.bucket}/${u.objectPath}`} className="border-b border-line/50">
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {new Date(u.timestamp).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-mono">{u.portalAccountId}</td>
                  <td className="py-2 pr-4 font-mono max-w-xs truncate" title={u.objectPath}>
                    {u.objectPath}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{bytes(u.sizeBytes)}</td>
                  <td className="py-2">
                    <button
                      className="btn-ghost"
                      disabled={busy === u.objectPath}
                      onClick={() => download(u)}
                    >
                      {busy === u.objectPath ? "…" : "Download"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Audit trail of admin/platform actions, filterable by account. §7 */
function Activity({ api }: { api: Api }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  function load() {
    api
      .activity(filter.trim() || undefined)
      .then((r) => setEvents(r.events))
      .catch(() => setError("Could not load activity."));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-ink">Activity</h2>
        <div className="flex items-center gap-2">
          <input
            className="input !w-56"
            placeholder="Filter by account ID"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="btn-ghost" onClick={load}>Refresh</button>
        </div>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {events && events.length === 0 && <p className="text-sm text-ink-muted">No activity recorded.</p>}
      {events && events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-dim">
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4">Action</th>
                <th className="py-2 pr-4">Actor</th>
                <th className="py-2 pr-4">Target</th>
                <th className="py-2">Result</th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs text-ink-muted">
              {events.map((e, i) => (
                <tr key={i} className="border-b border-line/50">
                  <td className="py-2 pr-4 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="py-2 pr-4">{e.action}</td>
                  <td className="py-2 pr-4">{e.actorEmail ?? "—"}</td>
                  <td className="py-2 pr-4 max-w-xs truncate">{e.target ?? "—"}</td>
                  <td className={`py-2 ${e.result === "denied" ? "text-danger" : "text-accent-strong"}`}>
                    {e.result}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
